"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RecordParser = void 0;
const SchDocTypes_1 = require("../SchDocTypes");
const CoordinateConverter_1 = require("../parse/CoordinateConverter");
const AltiumEnums_1 = require("../../common/AltiumEnums");
const RecordTypes_1 = require("./RecordTypes");
function convertTildeOneToNewline(text) {
    return text.replace(/~1/g, '\n');
}
function decodeNetLabelText(text) {
    return convertTildeOneToNewline(text).replace(/¦/g, '|');
}
class RecordParser {
    static parseRecord(recordType, index, ownerIndex, properties) {
        switch (recordType) {
            case SchDocTypes_1.RecordType.HEADER:
                return this.parseHeader(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.COMPONENT:
                return this.parseComponent(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.PIN:
                return this.parsePin(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.LABEL:
                return this.parseLabel(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.WIRE:
                return this.parseWire(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.BUS:
                return this.parseBus(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.BUS_ENTRY:
                return this.parseBusEntry(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.NET_LABEL:
                return this.parseNetLabel(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.LINE:
                return this.parseLine(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.RECTANGLE:
                return this.parseRectangle(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.SHEET_SYMBOL:
                return this.parseSheetSymbol(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.SHEET_ENTRY:
                return this.parseSheetEntry(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.POWER_PORT:
                return this.parsePowerPort(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.PORT:
                return this.parsePort(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.POLYLINE:
                return this.parsePolyline(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.JUNCTION:
                return this.parseJunction(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.TEXT_FRAME:
                return this.parseTextFrame(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.SHEET:
                return this.parseSheet(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.SHEET_NAME:
                return this.parseSheetName(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.FILE_NAME:
                return this.parseFileName(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.DESIGNATOR:
                return this.parseDesignator(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.PARAMETER:
                return this.parseParameter(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.BEZIER:
                return this.parseBezier(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.ARC:
                return this.parseArc(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.POLYGON:
                return this.parsePolygon(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.ELLIPSE:
                return this.parseEllipse(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.ELLIPTICAL_ARC:
                return this.parseEllipticalArc(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.ROUND_RECTANGLE:
                return this.parseRoundRectangle(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.HARNESS_CONNECTOR:
                return this.parseHarnessConnector(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.HARNESS_ENTRY:
                return this.parseHarnessEntry(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.HARNESS_TYPE:
                return this.parseHarnessType(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.SIGNAL_HARNESS:
                return this.parseSignalHarness(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.IMPLEMENTATION_LIST:
                return this.parseImplementationList(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.IMPLEMENTATION:
                return this.parseImplementation(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.IMAGE:
                return this.parseImage(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.TEMPLATE:
                return this.parseTemplate(index, ownerIndex, properties);
            case SchDocTypes_1.RecordType.COMPILE_MASK:
                return this.parseCompileMask(index, ownerIndex, properties);
            default:
                return this.parseUnknown(recordType, index, ownerIndex, properties);
        }
    }
    static parseStrictInteger(value) {
        if (value === undefined || !/^[+-]?\d+$/.test(value.trim())) {
            return NaN;
        }
        return parseInt(value, 10);
    }
    static parseHeader(index, ownerIndex, properties) {
        const record = {
            recordType: SchDocTypes_1.RecordType.HEADER,
            index,
            ownerIndex,
            properties,
            majorVersion: '',
            minorVersion: properties['minorversion']?.value ?? '',
            header: properties['header']?.value ?? '',
        };
        const majorVersionMatch = /Version\s+(\d+\.\d+)/.exec(record.header);
        if (majorVersionMatch) {
            record.majorVersion = majorVersionMatch[1];
        }
        return RecordTypes_1.HeaderRecordSchema.parse(record);
    }
    static parseComponentKind(value) {
        if (value === undefined) {
            return undefined;
        }
        const parsed = this.parseStrictInteger(value);
        if (Number.isNaN(parsed)) {
            return parsed;
        }
        return (0, AltiumEnums_1.isKnownComponentKind)(parsed) ? parsed : AltiumEnums_1.ComponentKind.Standard;
    }
    static getUnrecognizedComponentKind(value) {
        if (value === undefined) {
            return undefined;
        }
        const parsed = this.parseStrictInteger(value);
        return Number.isNaN(parsed) || (0, AltiumEnums_1.isKnownComponentKind)(parsed) ? undefined : parsed;
    }
    static parseComponent(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const record = {
            recordType: SchDocTypes_1.RecordType.COMPONENT,
            index,
            ownerIndex,
            properties,
            uniqueId: properties['uniqueid']?.value ?? '',
            libReference: properties['libreference']?.value ?? '',
            sourceLibraryName: properties['sourcelibraryname']?.value ?? '',
            componentDescription: properties['componentdescription']?.value ?? '',
            componentkind: this.parseComponentKind(properties['componentkind']?.value),
            unrecognizedComponentKind: this.getUnrecognizedComponentKind(properties['componentkind']?.value),
            allPinCount: parseInt(properties['allpincount']?.value ?? '0', 10),
            designItemId: properties['designitemid']?.value,
            itemGuid: properties['itemguid']?.value,
            revisionGuid: properties['revisionguid']?.value,
            symbolItemGuid: properties['symbolitemguid']?.value,
            symbolRevisionGuid: properties['symbolrevisionguid']?.value,
            symbolVaultGuid: properties['symbolvaultguid']?.value,
            vaultGuid: properties['vaultguid']?.value,
            targetFileName: properties['targetfilename']?.value,
            location,
            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
            isMirrored: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['ismirrored']?.value),
            partCount: parseInt(properties['partcount']?.value ?? '1', 10),
            displayModeCount: parseInt(properties['displaymodecount']?.value ?? '0', 10),
            displayMode: parseInt(properties['displaymode']?.value ?? '0', 10),
            currentPartId: parseInt(properties['currentpartid']?.value ?? '0', 10),
            ownerPartId: properties['ownerpartid']?.value,
            partIdLocked: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['partidlocked']?.value),
        };
        return RecordTypes_1.ComponentRecordSchema.parse(record);
    }
    static parsePin(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const pinLength = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'pinlength');
        const pinconglomerate = parseInt(properties['pinconglomerate']?.value ?? '0', 10);
        const orientation = (pinconglomerate & 0x03);
        const hidden = (pinconglomerate & 0x04) !== 0;
        const showPinName = (pinconglomerate & 0x08) !== 0;
        const showDesignator = (pinconglomerate & 0x10) !== 0;
        const locked = (pinconglomerate & 0x40) !== 0;
        const record = {
            recordType: SchDocTypes_1.RecordType.PIN,
            index,
            ownerIndex,
            properties,
            name: properties['name']?.value ?? '',
            designator: properties['designator']?.value ?? '',
            location,
            pinLength,
            orientation,
            electrical: parseInt(properties['electrical']?.value ?? '0', 10),
            hidden,
            showPinName,
            showDesignator,
            locked,
            ownerPartId: parseInt(properties['ownerpartid']?.value ?? '-1', 10),
            ownerPartDisplayMode: parseInt(properties['ownerpartdisplaymode']?.value ?? '0', 10),
        };
        return RecordTypes_1.PinRecordSchema.parse(record);
    }
    static parseWire(index, ownerIndex, properties) {
        const locationCount = parseInt(properties['locationcount']?.value ?? '0', 10);
        const points = [];
        for (let i = 1; i <= locationCount; i++) {
            points.push(CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, `x${i}`, `y${i}`));
        }
        const lineWidth = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'linewidth');
        const record = {
            recordType: SchDocTypes_1.RecordType.WIRE,
            index,
            ownerIndex,
            properties,
            points,
            lineWidth,
        };
        return RecordTypes_1.WireRecordSchema.parse(record);
    }
    static parseBus(index, ownerIndex, properties) {
        const locationCount = parseInt(properties['locationcount']?.value ?? '0', 10);
        const points = [];
        for (let i = 1; i <= locationCount; i++) {
            points.push(CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, `x${i}`, `y${i}`));
        }
        const lineWidth = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'linewidth');
        const record = {
            recordType: SchDocTypes_1.RecordType.BUS,
            index,
            ownerIndex,
            properties,
            points,
            lineWidth,
        };
        return RecordTypes_1.BusRecordSchema.parse(record);
    }
    static parseBusEntry(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const corner = CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, 'corner.x', 'corner.y');
        const record = {
            recordType: SchDocTypes_1.RecordType.BUS_ENTRY,
            index,
            ownerIndex,
            properties,
            location,
            corner,
        };
        return RecordTypes_1.BusEntryRecordSchema.parse(record);
    }
    static parseNetLabel(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const record = {
            recordType: SchDocTypes_1.RecordType.NET_LABEL,
            index,
            ownerIndex,
            properties,
            text: decodeNetLabelText(properties['text']?.value ?? ''),
            location,
            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
            fontId: parseInt(properties['fontid']?.value ?? '0', 10),
            justification: parseInt(properties['justification']?.value ?? '0', 10),
        };
        return RecordTypes_1.NetLabelRecordSchema.parse(record);
    }
    static parseLine(index, ownerIndex, properties) {
        const startLocation = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const endLocation = CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, 'corner.x', 'corner.y');
        const lineWidth = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'linewidth');
        const color = CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10));
        const record = {
            recordType: SchDocTypes_1.RecordType.LINE,
            index,
            ownerIndex,
            properties,
            startLocation,
            endLocation,
            lineWidth,
            color,
            ownerPartId: parseInt(properties['ownerpartid']?.value ?? '-1', 10),
            ownerPartDisplayMode: parseInt(properties['ownerpartdisplaymode']?.value ?? '0', 10),
        };
        return RecordTypes_1.LineRecordSchema.parse(record);
    }
    static parseRectangle(index, ownerIndex, properties) {
        const topLeft = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const bottomRight = CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, 'corner.x', 'corner.y');
        const lineWidth = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'linewidth');
        const color = CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10));
        const fillColor = CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10));
        const record = {
            recordType: SchDocTypes_1.RecordType.RECTANGLE,
            index,
            ownerIndex,
            properties,
            topLeft,
            bottomRight,
            lineWidth,
            color,
            fillColor,
            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
            isTransparent: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['transparent']?.value),
            isNotAccesible: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['isnotaccesible']?.value),
            indexInSheet: parseInt(properties['indexinsheet']?.value ?? '0', 10),
            ownerPartId: parseInt(properties['ownerpartid']?.value ?? '-1', 10),
            ownerPartDisplayMode: parseInt(properties['ownerpartdisplaymode']?.value ?? '0', 10),
            uniqueId: properties['uniqueid']?.value ?? '',
        };
        return RecordTypes_1.RectangleRecordSchema.parse(record);
    }
    static parsePolyline(index, ownerIndex, properties) {
        const locationCount = parseInt(properties['locationcount']?.value ?? '0', 10);
        const points = [];
        for (let i = 1; i <= locationCount; i++) {
            points.push(CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, `x${i}`, `y${i}`));
        }
        const lineWidth = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'linewidth');
        const color = CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10));
        const record = {
            recordType: SchDocTypes_1.RecordType.POLYLINE,
            index,
            ownerIndex,
            properties,
            points,
            lineWidth,
            color,
            isNotAccesible: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['isnotaccesible']?.value),
            indexInSheet: parseInt(properties['indexinsheet']?.value ?? '0', 10),
            ownerPartId: parseInt(properties['ownerpartid']?.value ?? '-1', 10),
            lineShapeSize: parseInt(properties['lineshapesize']?.value ?? '0', 10),
            uniqueId: properties['uniqueid']?.value ?? '',
        };
        return RecordTypes_1.PolylineRecordSchema.parse(record);
    }
    static parseDesignator(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const record = {
            recordType: SchDocTypes_1.RecordType.DESIGNATOR,
            index,
            ownerIndex,
            properties,
            name: convertTildeOneToNewline(properties['name']?.value ?? ''),
            text: convertTildeOneToNewline(properties['text']?.value ?? ''),
            location,
            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
            justification: parseInt(properties['justification']?.value ?? '0', 10),
            fontId: parseInt(properties['fontid']?.value ?? '0', 10),
        };
        return RecordTypes_1.DesignatorRecordSchema.parse(record);
    }
    static parsePowerPort(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const record = {
            recordType: SchDocTypes_1.RecordType.POWER_PORT,
            index,
            ownerIndex,
            properties,
            text: convertTildeOneToNewline(properties['text']?.value ?? ''),
            location,
            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
            style: parseInt(properties['style']?.value ?? '0', 10),
            showNetName: properties['shownetname']?.value !== 'F',
            isCrossSheetConnector: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['iscrosssheetconnector']?.value),
        };
        return RecordTypes_1.PowerPortRecordSchema.parse(record);
    }
    static parseParameter(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const record = {
            recordType: SchDocTypes_1.RecordType.PARAMETER,
            index,
            ownerIndex,
            properties,
            name: convertTildeOneToNewline(properties['name']?.value ?? ''),
            text: decodeNetLabelText(properties['text']?.value ?? ''),
            location,
            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
            justification: parseInt(properties['justification']?.value ?? '0', 10),
            isHidden: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['ishidden']?.value),
            isMirrored: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['ismirrored']?.value),
            isShowName: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['isshowname']?.value),
            fontId: parseInt(properties['fontid']?.value ?? '0', 10),
        };
        return RecordTypes_1.ParameterRecordSchema.parse(record);
    }
    static parseJunction(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const record = {
            recordType: SchDocTypes_1.RecordType.JUNCTION,
            index,
            ownerIndex,
            properties,
            location,
        };
        return RecordTypes_1.JunctionRecordSchema.parse(record);
    }
    static parseTextFrame(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const corner = CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, 'corner.x', 'corner.y');
        const textMargin = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'textmargin');
        const borderWidth = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'linewidth');
        const text = properties['text']?.value ?? '';
        const record = {
            recordType: SchDocTypes_1.RecordType.TEXT_FRAME,
            index,
            ownerIndex,
            properties,
            location,
            corner,
            text: text.replace(/~1/g, '\n').replace(/~2/g, '|'),
            fontId: parseInt(properties['fontid']?.value ?? '0', 10),
            isWordWrapped: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['wordwrap']?.value),
            clipToRect: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['cliptorect']?.value),
            showBorder: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['showborder']?.value),
            textMargin,
            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '0', 10)),
            textColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['textcolor']?.value ?? '0', 10)),
            borderColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
            borderWidth,
            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
            alignment: parseInt(properties['alignment']?.value ?? '1', 10),
        };
        return RecordTypes_1.TextFrameRecordSchema.parse(record);
    }
    static parseSheetSymbol(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const xSize = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'xsize');
        const ySize = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'ysize');
        const record = {
            recordType: SchDocTypes_1.RecordType.SHEET_SYMBOL,
            index,
            ownerIndex,
            properties,
            location,
            xSize,
            ySize,
            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '0', 10)),
            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
            uniqueId: properties['uniqueid']?.value ?? '',
        };
        return RecordTypes_1.SheetSymbolRecordSchema.parse(record);
    }
    static parseSheetEntry(index, ownerIndex, properties) {
        const distanceFromTop = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'distancefromtop');
        const record = {
            recordType: SchDocTypes_1.RecordType.SHEET_ENTRY,
            index,
            ownerIndex,
            properties,
            distanceFromTop,
            side: parseInt(properties['side']?.value ?? '0', 10),
            name: properties['name']?.value ?? '',
            harnessType: properties['harnesstype']?.value ?? '',
            ioType: parseInt(properties['iotype']?.value ?? '0', 10),
            style: parseInt(properties['style']?.value ?? '0', 10),
            textFontId: parseInt(properties['textfontid']?.value ?? '0', 10),
        };
        return RecordTypes_1.SheetEntryRecordSchema.parse(record);
    }
    static parsePort(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const width = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'width');
        const height = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'height');
        const record = {
            recordType: SchDocTypes_1.RecordType.PORT,
            index,
            ownerIndex,
            properties,
            name: properties['name']?.value ?? '',
            harnessType: properties['harnesstype']?.value ?? '',
            location,
            width,
            height,
            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '0', 10)),
            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
            textColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['textcolor']?.value ?? '0', 10)),
            fontId: parseInt(properties['textfontid']?.value ?? '0', 10),
            alignment: parseInt(properties['alignment']?.value ?? '0', 10),
            ioType: parseInt(properties['iotype']?.value ?? '0', 10),
            style: parseInt(properties['style']?.value ?? '0', 10),
        };
        return RecordTypes_1.PortRecordSchema.parse(record);
    }
    static parseSheet(index, ownerIndex, properties) {
        const customX = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'customx');
        const customY = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'customy');
        const record = {
            recordType: SchDocTypes_1.RecordType.SHEET,
            index,
            ownerIndex,
            properties,
            fontIdCount: parseInt(properties['fontidcount']?.value ?? '0', 10),
            useCustomSheet: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['usecustomsheet']?.value),
            customX,
            customY,
            sheetStyle: parseInt(properties['sheetstyle']?.value ?? '0', 10),
            workspaceOrientation: parseInt(properties['workspaceorientation']?.value ?? '0', 10),
        };
        return RecordTypes_1.SheetRecordSchema.parse(record);
    }
    static parseLabel(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const record = {
            recordType: SchDocTypes_1.RecordType.LABEL,
            index,
            ownerIndex,
            properties,
            location: { x: location.x, y: -location.y },
            text: convertTildeOneToNewline(properties['text']?.value ?? ''),
            fontId: parseInt(properties['fontid']?.value ?? '0', 10),
            isMirrored: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['ismirrored']?.value),
            justification: parseInt(properties['justification']?.value ?? '0', 10),
            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
            ownerPartId: properties['ownerpartid']?.value ? parseInt(properties['ownerpartid'].value, 10) : undefined,
        };
        return RecordTypes_1.LabelRecordSchema.parse(record);
    }
    static parseSheetName(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const record = {
            recordType: SchDocTypes_1.RecordType.SHEET_NAME,
            index,
            ownerIndex,
            properties,
            text: convertTildeOneToNewline(properties['text']?.value ?? ''),
            fontId: parseInt(properties['fontid']?.value ?? '1', 10),
            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
            location,
            isHidden: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['ishidden']?.value),
        };
        return RecordTypes_1.SheetNameRecordSchema.parse(record);
    }
    static parseFileName(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const record = {
            recordType: SchDocTypes_1.RecordType.FILE_NAME,
            index,
            ownerIndex,
            properties,
            text: convertTildeOneToNewline(properties['text']?.value ?? ''),
            fontId: parseInt(properties['fontid']?.value ?? '1', 10),
            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
            location,
            isHidden: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['ishidden']?.value),
        };
        return RecordTypes_1.FileNameRecordSchema.parse(record);
    }
    static parseBezier(index, ownerIndex, properties) {
        const locationCount = parseInt(properties['locationcount']?.value ?? '0', 10);
        const points = [];
        for (let i = 1; i <= locationCount; i++) {
            points.push(CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, `x${i}`, `y${i}`));
        }
        const record = {
            recordType: SchDocTypes_1.RecordType.BEZIER,
            index,
            ownerIndex,
            properties,
            points,
            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
            ownerPartId: properties['ownerpartid']?.value ? parseInt(properties['ownerpartid'].value, 10) : undefined,
            ownerPartDisplayMode: properties['ownerpartdisplaymode']?.value ? parseInt(properties['ownerpartdisplaymode'].value, 10) : undefined,
        };
        return RecordTypes_1.BezierRecordSchema.parse(record);
    }
    static parseArc(index, ownerIndex, properties) {
        const center = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const radius = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'radius');
        const record = {
            recordType: SchDocTypes_1.RecordType.ARC,
            index,
            ownerIndex,
            properties,
            center,
            radius,
            startAngle: parseFloat(properties['startangle']?.value ?? '0'),
            endAngle: parseFloat(properties['endangle']?.value ?? '0'),
            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10)),
            ownerPartId: properties['ownerpartid']?.value ? parseInt(properties['ownerpartid'].value, 10) : undefined,
            ownerPartDisplayMode: properties['ownerpartdisplaymode']?.value ? parseInt(properties['ownerpartdisplaymode'].value, 10) : undefined,
        };
        return RecordTypes_1.ArcRecordSchema.parse(record);
    }
    static parsePolygon(index, ownerIndex, properties) {
        const locationCount = parseInt(properties['locationcount']?.value ?? '0', 10);
        const points = [];
        for (let i = 1; i <= locationCount; i++) {
            points.push(CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, `x${i}`, `y${i}`));
        }
        const record = {
            recordType: SchDocTypes_1.RecordType.POLYGON,
            index,
            ownerIndex,
            properties,
            points,
            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10)),
            ownerPartId: properties['ownerpartid']?.value ? parseInt(properties['ownerpartid'].value, 10) : undefined,
            ownerPartDisplayMode: properties['ownerpartdisplaymode']?.value ? parseInt(properties['ownerpartdisplaymode'].value, 10) : undefined,
        };
        return RecordTypes_1.PolygonRecordSchema.parse(record);
    }
    static parseEllipse(index, ownerIndex, properties) {
        const center = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const radius = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'radius');
        const secondaryRadius = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'secondaryradius');
        const record = {
            recordType: SchDocTypes_1.RecordType.ELLIPSE,
            index,
            ownerIndex,
            properties,
            center,
            radius,
            secondaryRadius,
            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10)),
            ownerPartId: properties['ownerpartid']?.value ? parseInt(properties['ownerpartid'].value, 10) : undefined,
            ownerPartDisplayMode: properties['ownerpartdisplaymode']?.value ? parseInt(properties['ownerpartdisplaymode'].value, 10) : undefined,
        };
        return RecordTypes_1.EllipseRecordSchema.parse(record);
    }
    static parseEllipticalArc(index, ownerIndex, properties) {
        const center = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const radius = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'radius');
        const secondaryRadius = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'secondaryradius');
        const record = {
            recordType: SchDocTypes_1.RecordType.ELLIPTICAL_ARC,
            index,
            ownerIndex,
            properties,
            center,
            radius,
            secondaryRadius,
            startAngle: parseFloat(properties['startangle']?.value ?? '0'),
            endAngle: parseFloat(properties['endangle']?.value ?? '0'),
            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10)),
            ownerPartId: properties['ownerpartid']?.value ? parseInt(properties['ownerpartid'].value, 10) : undefined,
            ownerPartDisplayMode: properties['ownerpartdisplaymode']?.value ? parseInt(properties['ownerpartdisplaymode'].value, 10) : undefined,
        };
        return RecordTypes_1.EllipticalArcRecordSchema.parse(record);
    }
    static parseRoundRectangle(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const corner = CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, 'corner.x', 'corner.y');
        const cornerRadiusX = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'cornerxradius');
        const cornerRadiusY = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'corneryradius');
        const record = {
            recordType: SchDocTypes_1.RecordType.ROUND_RECTANGLE,
            index,
            ownerIndex,
            properties,
            location,
            corner,
            cornerRadius: { x: cornerRadiusX, y: cornerRadiusY },
            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10)),
            ownerPartId: properties['ownerpartid']?.value ? parseInt(properties['ownerpartid'].value, 10) : undefined,
            ownerPartDisplayMode: properties['ownerpartdisplaymode']?.value ? parseInt(properties['ownerpartdisplaymode'].value, 10) : undefined,
            transparent: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['transparent']?.value),
            isNotAccesible: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['isnotaccesible']?.value),
            lineStyle: parseInt(properties['linestyle']?.value ?? '0', 10),
        };
        return RecordTypes_1.RoundRectangleRecordSchema.parse(record);
    }
    static parseHarnessConnector(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const xSize = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'xsize');
        const ySize = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'ysize');
        const rawLineWidth = parseInt(properties['linewidth']?.value ?? '0', 10);
        const rawSide = properties['harnessconnectorside']?.value;
        const side = RecordParser.parseHarnessConnectorSide(rawSide);
        const record = {
            recordType: SchDocTypes_1.RecordType.HARNESS_CONNECTOR,
            index,
            ownerIndex,
            properties,
            location,
            xSize,
            ySize,
            lineWidth: Number.isNaN(rawLineWidth) ? 0 : rawLineWidth,
            isSolid: properties['issolid']?.value === undefined ? true : CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid'].value),
            side,
            primaryConnectionPosition: CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'primaryconnectionposition'),
            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10)),
        };
        return RecordTypes_1.HarnessConnectorRecordSchema.parse(record);
    }
    static parseHarnessEntry(index, ownerIndex, properties) {
        const distanceFromTop = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'distancefromtop');
        const record = {
            recordType: SchDocTypes_1.RecordType.HARNESS_ENTRY,
            index,
            ownerIndex,
            properties,
            distanceFromTop,
            side: parseInt(properties['side']?.value ?? '0', 10),
            name: properties['name']?.value ?? '',
            harnessType: properties['harnesstype']?.value ?? '',
            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10)),
            textColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['textcolor']?.value ?? '0', 10)),
            textFontId: parseInt(properties['textfontid']?.value ?? '0', 10),
        };
        return RecordTypes_1.HarnessEntryRecordSchema.parse(record);
    }
    static parseHarnessType(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const record = {
            recordType: SchDocTypes_1.RecordType.HARNESS_TYPE,
            index,
            ownerIndex,
            properties,
            text: convertTildeOneToNewline(properties['text']?.value ?? ''),
            location,
            isHidden: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['ishidden']?.value),
            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
            fontId: parseInt(properties['fontid']?.value ?? '0', 10),
        };
        return RecordTypes_1.HarnessTypeRecordSchema.parse(record);
    }
    static parseHarnessConnectorSide(rawSide) {
        switch (parseInt(rawSide ?? '0', 10)) {
            case 1:
                return AltiumEnums_1.SheetSymbolSide.Right;
            case 2:
                return AltiumEnums_1.SheetSymbolSide.Top;
            case 3:
                return AltiumEnums_1.SheetSymbolSide.Bottom;
            case 0:
            default:
                return AltiumEnums_1.SheetSymbolSide.Left;
        }
    }
    static parseSignalHarness(index, ownerIndex, properties) {
        const locationCount = parseInt(properties['locationcount']?.value ?? '0', 10);
        const points = [];
        for (let i = 1; i <= locationCount; i++) {
            points.push(CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, `x${i}`, `y${i}`));
        }
        const record = {
            recordType: SchDocTypes_1.RecordType.SIGNAL_HARNESS,
            index,
            ownerIndex,
            properties,
            points,
            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
        };
        return RecordTypes_1.SignalHarnessRecordSchema.parse(record);
    }
    static parseImplementationList(index, ownerIndex, properties) {
        const record = {
            recordType: SchDocTypes_1.RecordType.IMPLEMENTATION_LIST,
            index,
            ownerIndex,
            properties,
        };
        return RecordTypes_1.ImplementationListRecordSchema.parse(record);
    }
    static parseImplementation(index, ownerIndex, properties) {
        const record = {
            recordType: SchDocTypes_1.RecordType.IMPLEMENTATION,
            index,
            ownerIndex,
            properties,
            modelName: properties['modelname']?.value ?? '',
            modelType: properties['modeltype']?.value ?? '',
            dataFile: properties['modeldatafile0']?.value ?? '',
            description: properties['description']?.value ?? '',
            isCurrent: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['iscurrent']?.value),
        };
        return RecordTypes_1.ImplementationRecordSchema.parse(record);
    }
    static parseImage(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const corner = CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, 'corner.x', 'corner.y');
        const record = {
            recordType: SchDocTypes_1.RecordType.IMAGE,
            index,
            ownerIndex,
            properties,
            location,
            corner,
            filename: properties['filename']?.value ?? '',
            ownerPartId: parseInt(properties['ownerpartid']?.value ?? '-1', 10),
            ownerPartDisplayMode: parseInt(properties['ownerpartdisplaymode']?.value ?? '0', 10),
        };
        return RecordTypes_1.ImageRecordSchema.parse(record);
    }
    static parseTemplate(index, ownerIndex, properties) {
        const record = {
            recordType: SchDocTypes_1.RecordType.TEMPLATE,
            index,
            ownerIndex,
            properties,
            filename: properties['filename']?.value ?? '',
            isNotAccesible: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['isnotaccesible']?.value),
        };
        return RecordTypes_1.TemplateRecordSchema.parse(record);
    }
    static parseCompileMask(index, ownerIndex, properties) {
        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
        const corner = CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, 'corner.x', 'corner.y');
        const record = {
            recordType: SchDocTypes_1.RecordType.COMPILE_MASK,
            index,
            ownerIndex,
            properties,
            location,
            corner,
        };
        return RecordTypes_1.CompileMaskRecordSchema.parse(record);
    }
    static parseUnknown(recordType, index, ownerIndex, properties) {
        return {
            recordType,
            index,
            ownerIndex,
            properties,
        };
    }
}
exports.RecordParser = RecordParser;
//# sourceMappingURL=RecordParser.js.map